import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./HomePage.css";
import "./ProgramsPage.css";
import CheckMyPriceModal from "../components/CheckMyPriceModal";
import Logo from "../components/Logo";
import { LangToggle, useLang } from "../lib/i18n";
import { getCart } from "../lib/cart";
import { getContent } from "../lib/content";
import { getProgramsContent } from "../lib/programsContent";

/* Replica of antiagingbybb.com/programs */

function NavBar({ cartCount, c }) {
  return (
    <nav className="hp-nav">
      <Logo size="md" linkTo="/" testId="pp-nav-logo" />
      <ul className="hp-nav-links">
        <li><Link to="/" data-testid="pp-nav-home">{c.nav.home}</Link></li>
        <li><Link to="/#main-offer">{c.nav.tirzepatide}</Link></li>
        <li><Link to="/#how">{c.nav.how}</Link></li>
        <li><Link to="/#guides">{c.nav.guides}</Link></li>
        <li><Link to="/#contact">{c.nav.contact}</Link></li>
        <li><Link to="/estore" data-testid="pp-nav-shop">{c.nav.shop}</Link></li>
      </ul>
      <div className="hp-nav-right">
        <LangToggle />
        <Link to="/cart" className="hp-cart" aria-label="Cart" data-testid="pp-nav-cart">
          🛒{cartCount > 0 && <span className="hp-cart-badge">{cartCount}</span>}
        </Link>
      </div>
    </nav>
  );
}

function PPFooter({ c }) {
  return (
    <footer className="hp-footer" data-testid="pp-footer">
      <div className="hp-footer-grid">
        <div className="hp-footer-brand">
          <Logo size="lg" linkTo={null} testId="pp-footer-logo"/>
          <p className="hp-footer-tagline">{c.footer.tagline}</p>
          <div className="hp-footer-socials">
            <a href="#" aria-label="Instagram">IG</a>
            <a href="#" aria-label="Facebook">FB</a>
            <a href="#" aria-label="TikTok">TK</a>
          </div>
        </div>
        <div>
          <h4 className="hp-footer-h">{c.footer.navH}</h4>
          <ul>
            {c.footer.navItems.map((it,i)=><li key={i}><Link to={["/#main-offer","/#support","/#how","/#pricing","/#guides"][i]}>{it}</Link></li>)}
          </ul>
        </div>
        <div>
          <h4 className="hp-footer-h">{c.footer.gsH}</h4>
          <ul>
            <li><Link to="/estore">{c.footer.gsItems[0]}</Link></li>
            <li><Link to="/intake/start">{c.footer.gsItems[1]}</Link></li>
            <li><Link to="/estore">{c.footer.gsItems[2]}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="hp-footer-h">{c.footer.ctH}</h4>
          <ul>
            <li><a href="mailto:care@antiagingbybb.com">care@antiagingbybb.com</a></li>
            <li><a href="tel:+13055289525">+1 (305) 528 9525</a></li>
            <li>11098 Biscayne Blvd, Suite 401<br/>Miami FL 33161</li>
            <li>{c.footer.hours}</li>
          </ul>
        </div>
      </div>
      <div className="hp-footer-bottom">
        <span>{c.footer.copyright}</span>
        <span><a href="#">{c.footer.privacy}</a> | <a href="#">{c.footer.terms}</a></span>
      </div>
    </footer>
  );
}

// PROGRAMS array removed — now using pc.programs from getProgramsContent(lang)

// PRODUCTS and TREATMENTS arrays removed — now using pc.singles and pc.treatments from getProgramsContent(lang)

const ROUTE_MAP = {
  "tirzepatide-core": "/intake?program=tirzepatide-core",
  "metabolic-boost": "/intake?program=metabolic-boost",
  "total-body-optimization": "/intake?program=total-body-optimization",
  "advanced-transformation": "/intake?program=advanced-transformation",
  "tirzepatide": "/intake?product=tirzepatide",
  "nad": "/intake?product=nad",
  "sermorelin": "/intake?product=sermorelin",
};

export default function ProgramsPage() {
  const nav = useNavigate();
  const loc = useLocation();
  const [tab, setTab] = useState("all");
  const [cmpOpen, setCmpOpen] = useState(false);
  const [cartCount, setCartCount] = useState(getCart().reduce((s,i)=>s+i.qty,0));
  const { lang } = useLang();
  const c = getContent(lang);
  const pc = getProgramsContent(lang);
  const baSliderRef = useRef(null);
  const baAfterRef = useRef(null);
  const baHandleRef = useRef(null);

  useEffect(() => {
    const fn = () => setCartCount(getCart().reduce((s,i)=>s+i.qty,0));
    window.addEventListener("cart-updated", fn);
    return () => window.removeEventListener("cart-updated", fn);
  }, []);

  useEffect(() => { window.scrollTo(0, 0); }, [loc.pathname]);

  useEffect(() => {
    const slider = baSliderRef.current;
    const after = baAfterRef.current;
    const handle = baHandleRef.current;
    if (!slider || !after || !handle) return;
    const update = (clientX) => {
      const rect = slider.getBoundingClientRect();
      let pos = ((clientX - rect.left) / rect.width) * 100;
      pos = Math.max(0, Math.min(100, pos));
      after.style.clipPath = `inset(0 ${100 - pos}% 0 0)`;
      handle.style.left = `${pos}%`;
    };
    const onMove = (e) => update(e.clientX);
    const onTouch = (e) => update(e.touches[0].clientX);
    slider.addEventListener("mousemove", onMove);
    slider.addEventListener("touchmove", onTouch);
    return () => {
      slider.removeEventListener("mousemove", onMove);
      slider.removeEventListener("touchmove", onTouch);
    };
  }, []);

  const filtered = tab === "all" ? pc.treatments : pc.treatments.filter(t => t.cat === tab);

  return (
    <div className="hp-root" data-testid="programs-page">
      <NavBar cartCount={cartCount} c={c} />

      {/* PAGE HERO */}
      <section className="pp-hero">
        <div className="pp-hero-content">
          <div className="hp-eyebrow">{c.pp.heroEyebrow}</div>
          <h1 className="hp-h1">{c.pp.heroH1_pre}<br/><em>{c.pp.heroH1_em}</em></h1>
          <p className="hp-sub">{c.pp.heroSub}</p>
          <div className="pp-trust-row">
            {c.pp.heroTrust.map((t,i)=><span key={i}>✔ {t}</span>)}
          </div>
        </div>
        <div className="pp-hero-img">
          <img src="https://assets.cdn.filesafe.space/F6iXsZYsDLnX3HpNjkIU/media/69e364a82c135a8c830a2fdf.png" alt="Antiaging by BB — Medical Weight Loss Programs"/>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="hp-ticker">
        <div className="hp-ticker-track">
          {[...Array(3)].flatMap((_,i)=>c.ticker.map((t,j)=>(
            <span key={`${i}-${j}`} className="hp-ticker-item">{t}<span className="hp-ticker-sep">✦</span></span>
          )))}
        </div>
      </div>

      {/* PROGRAMS */}
      <section className="hp-section pp-programs" id="programs">
        <div className="hp-center">
          <div className="hp-eyebrow">{c.pp.step12}</div>
          <h2 className="hp-h2 hp-center" style={{margin:"0 auto 16px"}}>{c.pp.protoTitle_pre}<em>{c.pp.protoTitle_em}</em></h2>
          <p className="hp-subhead hp-center" style={{margin:"0 auto 28px"}}>{c.pp.protoSub}</p>
          <div className="pp-price-display">
            <span className="pp-from">{c.pp.startingAt}</span>
            <span className="pp-price">$347<sup>*</sup></span>
          </div>
          <p className="pp-price-sub">{c.pp.pricesVary}</p>
        </div>

        <div className="pp-eligibility-bar">
          <div>
            <div className="pp-elig-title">{c.pp.eligTitle}</div>
            <div className="pp-elig-sub">{c.pp.eligSub}</div>
          </div>
          <Link to="/intake/start" className="hp-btn-primary" data-testid="pp-eligibility-cta">{c.pp.eligCta}</Link>
        </div>

        <div className="pp-plans">
          {pc.programs.map(p => (
            <article key={p.slug} className={`pp-plan ${p.featured?"featured":""}`} data-testid={`plan-${p.slug}`}>
              <div className="pp-plan-img">
                <img src={p.img} alt={`${p.name} ${p.subname}`}/>
                <div className="pp-plan-img-overlay"/>
              </div>
              <div className="pp-plan-top">
                {p.badge ? (
                  <div className={`pp-plan-badge ${p.badge.style}`}>{p.badge.text}</div>
                ) : <div className="pp-plan-badge invisible">.</div>}
                <h3 className="pp-plan-name">{p.name}<br/><em>{p.subname}</em></h3>
                <p className="pp-plan-tagline">{p.tagline}</p>
              </div>
              <div className="pp-plan-section">
                <div className="pp-plan-section-label">{c.pp.includesLabel}</div>
                {p.includes.map((it,i)=>(
                  <div key={i} className="pp-plan-item">
                    <img src={it.img} alt=""/>
                    <div>
                      <div className="pp-plan-item-name">{it.name}</div>
                      <div className="pp-plan-item-dose">{it.dose}</div>
                    </div>
                  </div>
                ))}
                {p.extra && <div className="pp-plan-extra"><div style={{width:32,height:32,flexShrink:0}}/><div>{p.extra}</div></div>}
              </div>
              <div className="pp-plan-section">
                <div className="pp-plan-section-label">{c.pp.bestForLabel}</div>
                <p className="pp-plan-bestfor">{p.bestFor}</p>
              </div>
              <div className="pp-plan-section">
                <div className="pp-plan-section-label">{c.pp.resultsLabel}</div>
                <ul className="pp-plan-results">
                  {p.results.map((r,i) => <li key={i}>✔ {r}</li>)}
                </ul>
              </div>
              <div className="pp-plan-cta">
                <button className={`pp-plan-btn ${p.style}`} onClick={()=>nav(ROUTE_MAP[p.slug])} data-testid={`plan-cta-${p.slug}`}>{p.cta}</button>
                <p className="pp-plan-micro">{c.pp.planMicro}</p>
              </div>
            </article>
          ))}
        </div>

        <div className="pp-addons">
          {c.pp.addons.map((a,i)=>(
            <div key={i} className={`pp-addon ${i===1?"highlight":""}`}>
              <div className="pp-addon-icon">{a.i}</div>
              <div>
                <div className="pp-addon-name">{a.n}</div>
                <div className="pp-addon-detail">{a.d || "\u00A0"}</div>
                <div className="pp-addon-price">{a.p}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* INDIVIDUAL PRODUCTS */}
      <section className="hp-section" id="products">
        <div className="hp-center">
          <div className="hp-eyebrow">{c.pp.alacarteEyebrow}</div>
          <h2 className="hp-h2 hp-center" style={{margin:"0 auto 14px"}}>{c.pp.alacarteTitle_pre}<em>{c.pp.alacarteTitle_em}</em></h2>
          <div className="pp-gold-rule"/>
          <p className="hp-subhead hp-center">{c.pp.alacarteSub}</p>
        </div>

        {pc.singles.map(p => (
          <article key={p.slug} className={`pp-product ${p.flip?"flip":""}`} data-testid={`product-${p.slug}`}>
            <div className="pp-prod-img">
              <img src={p.img} alt={p.name}/>
              <div className="pp-prod-img-overlay"/>
              <div className="pp-prod-img-tag">{p.imgTag}</div>
            </div>
            <div className="pp-prod-body">
              <div className="pp-prod-cat">{p.cat}</div>
              <h3 className="pp-prod-name">{p.name}<br/><em>{p.emName}</em></h3>
              <p className="pp-prod-headline">{p.headline}</p>
              <p className="pp-prod-desc">{p.desc}</p>
              <div className="pp-prod-benefits">
                {p.benefits.map((b,i) => <span key={i} className="pp-prod-benefit">{b}</span>)}
              </div>
              <div className="pp-prod-meta">
                <div><strong>{c.pp.whoLabel}</strong> {p.who}</div>
                <div><strong>{c.pp.howLabel}</strong> {p.how}</div>
                <div><strong>{c.pp.includedLabel_single}</strong> {p.included}</div>
              </div>
              <div className="pp-prod-actions">
                <button className="hp-btn-primary" onClick={()=>nav(ROUTE_MAP[p.slug])} data-testid={`product-cta-${p.slug}`}>{p.cta}</button>
                <a className="hp-btn-ghost" href={p.guideUrl} target="_blank" rel="noreferrer">{c.pp.freeGuide}</a>
              </div>
            </div>
          </article>
        ))}
      </section>

      {/* BEFORE/AFTER SLIDER */}
      <section className="estore-before-after-section">
        <div className="ba-slider" ref={baSliderRef}>
          <img className="ba-before" src="https://assets.cdn.filesafe.space/F6iXsZYsDLnX3HpNjkIU/media/69f50f2f07cf68d3e5a56d29.png" alt="Before treatment" />
          <img className="ba-after" ref={baAfterRef} src="https://assets.cdn.filesafe.space/F6iXsZYsDLnX3HpNjkIU/media/69f50f2fdd67e758c08b2da5.png" alt="After treatment" />
          <div className="ba-label ba-label-before">{c.pp.beforeLabel}</div>
          <div className="ba-label ba-label-after">{c.pp.afterLabel}</div>
          <div className="ba-handle" ref={baHandleRef}>
            <div className="ba-handle-btn">
              <div className="ba-handle-arrows">&#8249; &#8250;</div>
            </div>
          </div>
        </div>
      </section>

      {/* IN-CLINIC TREATMENTS */}
      <section className="hp-section" id="treatments">
        <div className="pp-treat-hdr">
          <div>
            <div className="hp-eyebrow">{c.pp.treatEyebrow}</div>
            <h2 className="hp-h2">{c.pp.treatTitle_pre}<br/><em>{c.pp.treatTitle_em}</em></h2>
          </div>
        </div>
        <div className="pp-tabs" data-testid="treatment-tabs">
          {c.pp.tabs.map(t => (
            <button key={t.id} className={`pp-tab ${tab===t.id?"active":""}`} onClick={()=>setTab(t.id)} data-testid={`tab-${t.id}`}>
              {t.label}{t.id==="all"?` (${pc.treatments.length})`:""}
            </button>
          ))}
        </div>
        <div className="pp-svc-grid" data-testid="services-grid">
          {filtered.map(s => (
            <article key={s.num} className="pp-svc-card" data-testid={`svc-${s.bookSlug}`}>
              <div className="pp-svc-photo">
                <img src={s.img} alt={s.name}/>
                <span className="pp-svc-num">{s.num}</span>
              </div>
              <div className="pp-svc-body">
                <span className="pp-svc-icon">{s.icon}</span>
                <div className="pp-svc-name">{s.name}</div>
                <div className="pp-svc-meta">
                  <span className="pp-svc-price">{s.price}{s.priceSub && <span className="pp-svc-price-sub">{s.priceSub}</span>}</span>
                  <span className="pp-svc-dur">{s.dur}</span>
                </div>
                <p className="pp-svc-desc">{s.desc}</p>
                <Link to={`/appointments/${s.bookSlug}`} className="pp-svc-link" data-testid={`book-${s.bookSlug}`}>{c.pp.bookCta}</Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="hp-section">
        <div className="hp-center">
          <div className="hp-eyebrow">{c.pp.compareEyebrow}</div>
          <h2 className="hp-h2 hp-center" style={{margin:"0 auto 18px"}}>{c.pp.compareTitle_pre}<em>{c.pp.compareTitle_em}</em></h2>
          <div className="pp-gold-rule"/>
        </div>
        <div className="pp-table-wrap">
          <table className="pp-compare">
            <thead><tr><th>{c.pp.compTable.feature}</th><th>NAD+</th><th>{c.pp.compTable.tirzepatideHeader}</th><th>{c.pp.compTable.sermorelinHeader}</th></tr></thead>
            <tbody>
              <tr><td>{c.pp.compTable.primaryGoal}</td><td>{c.pp.compTable.nadGoal}</td><td>{c.pp.compTable.tirzGoal}</td><td>{c.pp.compTable.sermGoal}</td></tr>
              <tr><td>{c.pp.compTable.startingPrice}</td><td className="hl">{c.pp.compTable.nadPrice}</td><td className="hl">{c.pp.compTable.tirzPrice}</td><td className="hl">{c.pp.compTable.sermPrice}</td></tr>
              <tr><td>{c.pp.compTable.skinBenefits}</td><td className="hl">{c.pp.compTable.nadSkin}</td><td>{c.pp.compTable.tirzSkin}</td><td className="hl">{c.pp.compTable.sermSkin}</td></tr>
              <tr><td>{c.pp.compTable.weightLoss}</td><td>{c.pp.compTable.nadWeight}</td><td className="hl">{c.pp.compTable.tirzWeight}</td><td>{c.pp.compTable.sermWeight}</td></tr>
              <tr><td>{c.pp.compTable.energy}</td><td className="hl">{c.pp.compTable.nadEnergy}</td><td>{c.pp.compTable.tirzEnergy}</td><td className="hl">{c.pp.compTable.sermEnergy}</td></tr>
              <tr><td>{c.pp.compTable.muscleBuilding}</td><td>{c.pp.compTable.nadMuscle}</td><td>{c.pp.compTable.tirzMuscle}</td><td className="hl">{c.pp.compTable.sermMuscle}</td></tr>
              <tr><td>{c.pp.compTable.sleepQuality}</td><td>{c.pp.compTable.nadSleep}</td><td>{c.pp.compTable.tirzSleep}</td><td className="hl">{c.pp.compTable.sermSleep}</td></tr>
              <tr><td>{c.pp.compTable.atHome}</td><td>{c.pp.compTable.yes}</td><td>{c.pp.compTable.yes}</td><td>{c.pp.compTable.yes}</td></tr>
              <tr><td>{c.pp.compTable.prescriptionReq}</td><td>{c.pp.compTable.supervised}</td><td>{c.pp.compTable.supervised}</td><td>{c.pp.compTable.supervised}</td></tr>
            </tbody>
          </table>
        </div>
        <p className="pp-table-foot">{c.pp.compTable.pricesVaryFootnote}</p>
      </section>

      <CheckMyPriceModal open={cmpOpen} onClose={()=>setCmpOpen(false)} />
      <PPFooter c={c} />
    </div>
  );
}
